import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/de';

import { 
    Dialog, DialogTitle, DialogContent, DialogActions, 
    Button, Box, Typography, TextField, Autocomplete, 
    ToggleButton, ToggleButtonGroup, Stack, Divider, 
    IconButton, Paper,
} from '@mui/material';
import axios from 'axios';
import { GiTennisCourt } from "react-icons/gi";
import { useAuth } from '../provider/auth';
import { FaUser } from "react-icons/fa";
import { MdClose, MdDelete, MdBlock, MdCheckCircle } from "react-icons/md";
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../App.js';
import ConfirmModal from './ConfirmModal';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Europe/Berlin");

function BookingModal({ show, onHide, free, slot, court, data, players, events, profile }) {
    const [confirmConfig, setConfirmConfig] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: () => {},
        type: 'question'
    });
    const { user } = useAuth();

    const [gameMode, setGameMode] = useState("single");
    const [duration, setDuration] = useState(1);
    const [teammates, setTeammates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [members, setMembers] = useState([]);
    const [player, setPlayer] = useState(null);
    
    // Check if the slot has already started or passed
    const now = dayjs();
    const slotStart = dayjs(data?.startTime || slot);
    const notStartedYet = slotStart.isAfter(now);

    useEffect(() => {
        if (profile && profile.player) {
            const familyMembersData = (profile.familyMembers || []).map(member => ({
                id: member._id,
                name: `${member.firstName} ${member.lastName}`,
            }));
            const playerData = {
                id: profile.player._id,
                name: `${profile.player.firstName} ${profile.player.lastName}`,
            };
            setMembers([...familyMembersData, playerData]);
            setPlayer(playerData);
        }
    }, [profile, players]);

    useEffect(() => {
        const numberOfTeammates = gameMode === "single" ? 1 : 3;
        if (gameMode === "single") {
            setDuration(1);
        }
        setTeammates(prev => {
            const firstPartner = prev[0] || null;
            const newTeammates = Array.from({ length: numberOfTeammates }, () => null);
            if (firstPartner) {
                newTeammates[0] = firstPartner;
            }
            return newTeammates;
        });
    }, [gameMode]);

    function getNameById(id, fallback) {
        if (!id) return fallback || "Unbekannt";
        if (typeof id === 'object') return (id.name || `${id.firstName} ${id.lastName}`).trim() || fallback || "Unbekannt";
        const found = players?.find(p => String(p.id) === String(id));
        return found ? found.name.trim() : (fallback || "Unbekannt");
    }

    const handleDelete = (id) => {
        setConfirmConfig({
            open: true,
            title: 'Buchung löschen',
            message: 'Möchtest du diese Buchung wirklich löschen?',
            onConfirm: () => executeDelete(id),
            type: 'delete'
        });
    };

    const executeDelete = async (id) => {
        setLoading(true);
        try {
            await axios.delete(`${API_BASE_URL}/api/bookings/${id}`);
            toast.success("Buchung erfolgreich gelöscht");
            onHide(true);
        } catch (error) {
            toast.error(error.response?.data?.message || "Fehler beim Löschen");
        } finally {
            setLoading(false);
        }
    };

    const handleBooking = async () => {
        const validTeammates = teammates.filter(t => t && t.id);
        if (validTeammates.length === 0) return toast.error("Wähle Mitspieler aus");
        if (gameMode === "double" && validTeammates.length !== 3) return toast.error("Wähle drei Mitspieler aus");
        if (!player) return toast.error("Bitte wähle einen Spieler aus.");
        
        // Final safety check for past bookings
        if (dayjs(slot).isBefore(dayjs())) {
            return toast.error("Du kannst keine Termine in der Vergangenheit buchen.");
        }

        const booking = {
            mainPlayer: user.playerId,
            playerId: player?.id || player?._id || user.playerId,
            courtId: court,
            startTime: slot.toISOString(),
            endTime: dayjs.tz(slot).add(duration, "hour").toISOString(),
            gameType: gameMode,
            partners: validTeammates.map(t => t.id || t._id)
        };

        setLoading(true);
        try {
            const res = await axios.post(`${API_BASE_URL}/api/booking`, booking);
            toast.success(res.data.message || "Buchung erfolgreich");
            onHide(true);
        } catch (error) {
            toast.error(error.response?.data?.message || "Fehler bei der Buchung");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateBookingStatus = (bookingId, newStatus) => {
        const action = newStatus === 1 ? "stornieren" : "reaktivieren";
        setConfirmConfig({
            open: true,
            title: `Termin ${action}`,
            message: `Möchtest du diesen Termin wirklich ${action}?`,
            onConfirm: () => executeUpdateBookingStatus(bookingId, newStatus),
            type: 'warning'
        });
    };

    const executeUpdateBookingStatus = async (bookingId, newStatus) => {
        setLoading(true);
        try {
            await axios.put(`${API_BASE_URL}/api/bookings/${bookingId}`, { status: newStatus });
            toast.success(`Termin erfolgreich ${newStatus === 1 ? 'storniert' : 'reaktiviert'}`);
            onHide(true);
        } catch (error) {
            toast.error(error.response?.data?.message || "Fehler bei Statusänderung");
        } finally {
            setLoading(false);
        }
    };

    const getAvailablePlayers = () => {
        if (!players) return [];
        const selectedIds = teammates.filter(t => t?.id).map(t => String(t.id));
        const guestEmail = (process.env.REACT_APP_GUEST_EMAIL || 'gast@tc-opfingen.de').toLowerCase();
        
        return players.filter(p => {
            if (!p?.id && !p?._id) return false;
            const pid = p.id || p._id;
            const currentSelectedPlayerId = player?.id || player?._id;
            if (String(pid) === String(currentSelectedPlayerId)) return false;
            if (p.email?.toLowerCase() === guestEmail) return true;
            return !selectedIds.includes(String(pid));
        }).sort((a, b) => {
            const aIsGuest = a.email?.toLowerCase() === guestEmail;
            const bIsGuest = b.email?.toLowerCase() === guestEmail;
            if (aIsGuest && !bIsGuest) return -1;
            if (!aIsGuest && bIsGuest) return 1;
            return (a.name || '').localeCompare(b.name || '');
        });
    };

    return (
        <Dialog open={show} onClose={() => onHide()} fullWidth maxWidth="xs">
            <DialogTitle sx={{ 
                bgcolor: 'primary.main', color: 'white', px: 3, py: 2,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <GiTennisCourt size={28} />
                    <Typography variant="h6" fontWeight="bold">Platz {court}</Typography>
                </Box>
                <IconButton onClick={() => onHide()} sx={{ color: 'white' }} size="small">
                    <MdClose />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 3, pt: 1 }}>
                <Stack spacing={4} sx={{ mt: 3 }}>
                    {free ? (
                        <>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', textTransform: 'uppercase', textAlign: 'center' }}>
                                    Buchungsdetails
                                </Typography>
                                
                                <Stack spacing={1.5}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">Datum:</Typography>
                                        <Typography variant="body2" fontWeight="bold">
                                            {dayjs(slot).tz().format("DD.MM.YYYY")}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">Zeitraum:</Typography>
                                        <Typography variant="body2" fontWeight="bold">
                                            {dayjs(slot).tz().format("HH:mm")} - {dayjs(slot).tz().add(duration, "hour").format("HH:mm")} Uhr
                                        </Typography>
                                    </Box>

                                    <Divider sx={{ my: 0.5 }} />

                                    <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, textAlign: 'center' }}>SPIELMODUS</Typography>
                                            <ToggleButtonGroup
                                                value={gameMode}
                                                exclusive
                                                onChange={(e, val) => val && setGameMode(val)}
                                                size="small"
                                                color="primary"
                                                fullWidth
                                            >
                                                <ToggleButton value="single" sx={{ py: 1 }}>Einzel</ToggleButton>
                                                <ToggleButton value="double" sx={{ py: 1 }}>Doppel</ToggleButton>
                                            </ToggleButtonGroup>
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, textAlign: 'center' }}>DAUER</Typography>
                                            <ToggleButtonGroup
                                                value={duration}
                                                exclusive
                                                onChange={(e, val) => val && setDuration(val)}
                                                size="small"
                                                color="primary"
                                                fullWidth
                                            >
                                                <ToggleButton value={1} sx={{ py: 1 }}>1 Std.</ToggleButton>
                                                {gameMode === "double" && <ToggleButton value={2} sx={{ py: 1 }}>2 Std.</ToggleButton>}
                                            </ToggleButtonGroup>
                                        </Box>
                                    </Stack>
                                </Stack>
                            </Paper>

                            {/* Members Section */}
                            <Box sx={{ px: 1, mt: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>SPIELER</Typography>

                                {members.length > 1 ? (
                                    <Autocomplete
                                        size="small"
                                        options={members}
                                        getOptionLabel={(o) => (o.name || "").trim()}
                                        value={player}
                                        onChange={(e, val) => setPlayer(val)}
                                        renderInput={(p) => <TextField {...p} label="Hauptspieler" />}
                                        sx={{ mb: 2 }}
                                        disableClearable={true}
                                    />
                                ) : (
                                    <Paper variant="outlined" sx={{ mb: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.05)' }}>
                                            <FaUser color="#666" size={12} />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>Hauptspieler</Typography>
                                            <Typography variant="body2" fontWeight="bold">
                                                {player?.name || getNameById(profile?.player?._id)}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                )}

                                <Stack spacing={2}>
                                    {teammates.map((t, i) => (
                                        <Autocomplete
                                            key={i}
                                            size="small"
                                            options={getAvailablePlayers()}
                                            getOptionLabel={(o) => (o?.name || "").trim()}
                                            value={t}
                                            onChange={(e, val) => {
                                                const nt = [...teammates];
                                                nt[i] = val;
                                                setTeammates(nt);
                                            }}
                                            renderInput={(p) => <TextField {...p} label={`Mitspieler ${i + 1}`} />}
                                        />
                                    ))}
                                </Stack>

                                {teammates.some(t => t?.email?.toLowerCase() === (process.env.REACT_APP_GUEST_EMAIL || 'gast@tc-opfingen.de').toLowerCase()) && (() => {
                                    const guestCount = teammates.filter(t => t?.email?.toLowerCase() === (process.env.REACT_APP_GUEST_EMAIL || 'gast@tc-opfingen.de').toLowerCase()).length;
                                    const rate = gameMode === 'single' ? 5 : 2.5;
                                    const totalPrice = (guestCount * duration * rate).toFixed(2).replace('.', ',');
                                    
                                    return (
                                        <Box sx={{ 
                                            mt: 3, p: 2, bgcolor: 'rgba(211, 47, 47, 0.05)', 
                                            borderLeft: '4px solid #d32f2f', borderRadius: '8px'
                                        }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                <Typography variant="caption" color="error" sx={{ fontWeight: 'bold' }}>GASTSPIELER-GEBÜHR</Typography>
                                                <Typography variant="subtitle2" color="error" sx={{ fontWeight: 'bold' }}>{totalPrice} €</Typography>
                                            </Box>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                                Hinweis: Platzgebühr wird automatisch für den Hauptspieler erfasst.
                                            </Typography>
                                        </Box>
                                    );
                                })()}
                            </Box>
                        </>
                    ) : (
                        data && (
                            <Box sx={{ textAlign: 'center', py: 1 }}>
                                {(() => {
                                    const eventObj = data.eventId ? events?.find(item => item._id === data.eventId) : null;
                                    return (
                                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', textTransform: 'uppercase', textAlign: 'center' }}>
                                                {data.eventId ? (eventObj?.name || "Termin-Details") : "Buchungsdetails"}
                                            </Typography>
                                            
                                            <Stack spacing={1.5}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" color="text.secondary">Datum:</Typography>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {dayjs(data.startTime).tz().format("DD.MM.YYYY")}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" color="text.secondary">Zeitraum:</Typography>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {dayjs(data.startTime).tz().format("HH:mm")} - {dayjs(data.endTime).tz().format("HH:mm")} Uhr
                                                    </Typography>
                                                </Box>
                                                {!data.eventId && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">Modus:</Typography>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {data.gameType === "single" ? "Einzel" : "Doppel"}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {data.eventId && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {eventObj?.courts?.length === 1 ? 'Platz:' : 'Plätze:'}
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {eventObj?.courts ? [...eventObj.courts].sort((a, b) => a - b).join(', ') : court}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {data.eventId && eventObj?.coach && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">Trainer:</Typography>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {eventObj.coach}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {!data.eventId && (
                                                    <Box sx={{ mt: 1, textAlign: 'left' }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold', textTransform: 'uppercase' }}>
                                                            Spieler
                                                        </Typography>
                                                        <Paper sx={{ p: 1.5, bgcolor: 'white', borderRadius: 2 }}>
                                                            <Stack spacing={0.5}>
                                                                <Typography variant="body2" fontWeight="500">
                                                                    {getNameById(data.playerId, data.playerSnapshot)}
                                                                </Typography>
                                                                {data.partners?.map((p, idx) => (
                                                                    <Typography key={idx} variant="body2">
                                                                        {getNameById(p, data.partnersSnapshot?.[idx])}
                                                                    </Typography>
                                                                ))}
                                                            </Stack>
                                                        </Paper>
                                                    </Box>
                                                )}
                                            </Stack>
                                        </Paper>
                                    );
                                })()}
                            </Box>
                        )
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2.5, bgcolor: '#f8f9fa' }}>
                <Box sx={{ width: '100%', display: 'flex', gap: 1.5, justifyContent: 'center' }}>
                    {data && data.eventId && user.role === 2 && notStartedYet && (
                        <Button 
                            variant="contained" 
                            color={data.status === 1 ? "success" : "warning"}
                            onClick={() => handleUpdateBookingStatus(data._id, data.status === 1 ? 0 : 1)}
                            disabled={loading}
                            startIcon={data.status === 1 ? <MdCheckCircle /> : <MdBlock />}
                            fullWidth
                        >
                            {data.status === 1 ? "Reaktivieren" : "Stornieren"}
                        </Button>
                    )}
                    
                    {data && !data.eventId && notStartedYet && (
                        (data.playerId === user.playerId || data.mainPlayer === user.playerId || 
                        data.partners?.includes(user.playerId) || user.role === 2)
                    ) && (
                        <Button 
                            variant="outlined" 
                            color="error" 
                            onClick={() => handleDelete(data._id)}
                            disabled={loading}
                            startIcon={<MdDelete />}
                            fullWidth
                        >
                            Löschen
                        </Button>
                    )}

                    {free && notStartedYet && (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleBooking}
                            disabled={loading}
                            fullWidth
                            sx={{ fontWeight: 'bold' }}
                        >
                            {loading ? "Wird gebucht..." : "Jetzt buchen"}
                        </Button>
                    )}

                    <Button 
                        variant="text" 
                        color="inherit" 
                        onClick={() => onHide()}
                        fullWidth
                    >
                        {free ? "Abbrechen" : "Schließen"}
                    </Button>
                </Box>
            </DialogActions>

            <ConfirmModal 
                open={confirmConfig.open}
                onClose={() => setConfirmConfig({ ...confirmConfig, open: false })}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
            />
        </Dialog>
    );
}

export default BookingModal;
